import enUs from 'date-fns/locale/en-US';
import * as React from 'react';
import * as ReactDatePickerNS from 'react-datepicker';
import type ReactDatePickerType from 'react-datepicker';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DatePicker = ((ReactDatePickerNS as any).default?.default ?? (ReactDatePickerNS as any).default ?? ReactDatePickerNS) as typeof ReactDatePickerType;

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import Tooltip from 'af-components/Tooltip';

import styles from './styles.module.scss';

interface OwnProps {
	selected: Date;
	onChange: (selected: Date, startDate: Date, endDate: Date) => void;
}

const WeeklyPicker: React.FC<OwnProps> = ({ selected, onChange }) => {
	const [weekDays, setWeekDaysState] = React.useState<Date[]>([]);
	const datepickerRef = React.useRef<ReactDatePickerType>(null);

	const setWeekDays = React.useCallback(
		(value: Date, sendChange: boolean) => {
			// Check if the provided date already exists in weekDays (compare by time)
			if (weekDays.some((day) => day.getTime() === value.getTime())) {
				return;
			}

			const momentDate = TimeUtils.parseMoment(value);
			if (!momentDate) {
				throw new Error('Incorrect date provided');
			}

			const startDate = momentDate.clone().startOf('week');
			const endDate = momentDate.clone().endOf('week');
			const newWeekDays: Date[] = [];

			for (let i = 0; i < 7; i++) {
				newWeekDays.push(startDate.clone().add(i, 'days').toDate());
			}

			setWeekDaysState(newWeekDays);

			if (sendChange) {
				onChange(value, startDate.toDate(), endDate.toDate());
			}
		},
		[weekDays, onChange]
	);

	const tooltipMessage = React.useMemo(() => `Period: ${TimeUtils.formatDate(weekDays[0], TimeFormat.DAY_WITH_DATE)} - ${TimeUtils.formatDate(weekDays[6], TimeFormat.DAY_WITH_DATE)}`, [weekDays]);

	// Mimic componentDidMount
	React.useEffect(() => {
		setWeekDays(selected, false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onDateChange = React.useCallback((date: Nullable<Date>) => {
		setWeekDays(date ?? new Date(), true);
	},
		[setWeekDays]
	);

	const focusDatePicker = React.useCallback(() => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		if ((datepickerRef.current as any)?.input) {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(datepickerRef.current as any).input.focus();
		}
	}, []);

	return (
		<div className={styles['date-picker-wrapper']}>
			<div className={`${styles['date-picker-wrapper__medium-picker']} week-picker`}>
				<Tooltip message={tooltipMessage} >
					<DatePicker
						className={styles.picker}
						dateFormat={TimeUtils.datePickerFormat(TimeFormat.DATE_ONLY)}
						disabledKeyboardNavigation
						dropdownMode="select"
						highlightDates={weekDays}
						locale={enUs}
						onChange={onDateChange}
						ref={datepickerRef}
						selected={selected}
						showMonthDropdown={true}
						showYearDropdown={true}
						todayButton="Today"
					/>
				</Tooltip>
			</div>
			<div className={styles['calendar-icon']} onClick={focusDatePicker}>
				<span className="icon-calendar" />
			</div>
		</div>
	);
};

export default React.memo(WeeklyPicker);
